import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import {
  SetPublishDimensions,
  GetPublishIFrame,
  GetPublishURL,
  GetPublishIFrameSuccess,
  GetPublishIFrameFail,
  SetPublishing,
  GetPublishImageCode,
  GetPublishImageCodeSuccess,
  GetPublishImageCodeFail,
  SavePublishImage,
  SavePublishImageSuccess,
  SavePublishImageFail,
  GetPublishURLSuccess,
} from '../../state/publish.actions';
import { PublishState } from '../../state/publish.state';
import { Observable, Subject, of } from 'rxjs';
import { ScreenDimensions } from 'src/app/shared/models/screen.dimensions.model';
import { ColorModel } from 'src/app/edit/models/color.model';
import { WidgetOptions } from '../../models/widget-options.model';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { debounceTime, takeUntil, flatMap, distinctUntilChanged } from 'rxjs/operators';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { PublishService } from '../../publish.service';
import { StorageService, LocalStorageEnum } from 'src/app/shared/storage.service';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { CopyClipboardType, ClipboardService } from 'src/app/shared/clipboard.service';
import { DialogService } from 'src/app/dialogs/dialog.service';
import {
  PublishWidgetComponent,
  PublishWidgetOptions,
} from '../publish-widget/publish-widget.component';
import { ScreenModel } from 'src/app/shared/models/screen.model';
import { ImageTypeEnum } from 'src/app/shared/models/image-type.enum';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { delay, get } from 'lodash';
import { invalidateLocalWidgetOptions } from 'src/app/shared/storage-utils';
import { defaultViewerViewLoader, ViewLoaderEnum } from 'src/app/view/models/view-loader.enum';
import { TabsComponent, TabItem } from 'src/app/shared/components/tabs/tabs/tabs.component';
import { SnapshotOptions } from '../../models/snapshot-options.model';
import { plainToClass } from 'class-transformer';
import { ExportImageUtils } from 'src/app/shared/libraries/image/export-image';
import { StoryService } from 'src/app/story/story.service';
import { QuizQuestion } from 'src/app/quiz/models/quiz-question.model';
import { ViewQuizModeEnum } from 'src/app/view/models/view-quiz-mode.enum';
import { createQuestionsFromChapters } from 'src/app/quiz/config/quiz';
import { BookmarkFlagsEnum } from 'src/app/bookmarks/models/bookmark.flags.enum';

const snapshotSmall: ScreenModel = {
  title: 'Small',
  type: 'desktop',
  dimensions: {
    width: 480,
    height: 320,
  },
  orientation: 'horizontal',
};

const snapshotMedium: ScreenModel = {
  title: 'Medium',
  type: 'desktop',
  dimensions: {
    width: 700,
    height: 550,
  },
  orientation: 'horizontal',
};

const snapshotLarge: ScreenModel = {
  title: 'Large',
  type: 'desktop',
  dimensions: {
    width: 1920,
    height: 1200,
  },
  orientation: 'horizontal',
};

const snapshotExtraLarge: ScreenModel = {
  title: 'Extra Large',
  type: 'desktop',
  dimensions: {
    width: 2560,
    height: 1440,
  },
  orientation: 'horizontal',
};

const snapshotMax: ScreenModel = {
  title: 'Maximum',
  type: 'desktop',
  dimensions: {
    width: 5120,
    height: 2880,
  },
  orientation: 'horizontal',
};

const ppi = 300; // Pixels per inch

@Component({
  selector: 'app-publish',
  templateUrl: './publish.component.html',
  styleUrls: ['./publish.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublishComponent implements OnInit, OnDestroy {
  @ViewChild('imageTabs', { static: false, read: TabsComponent })
  imageTabs: TabsComponent;

  // snapshot state
  readonly minSnapshotPixels: ScreenDimensions = {
    width: 300,
    height: 300,
  };

  readonly maxSnapshotPixels: ScreenDimensions = {
    width: 5120,
    height: 2880,
  };

  readonly minSnapshotInches: ScreenDimensions = {
    width: this.pixelsToInches(this.minSnapshotPixels.width),
    height: this.pixelsToInches(this.minSnapshotPixels.height),
  };

  readonly maxSnapshotInches: ScreenDimensions = {
    width: this.pixelsToInches(this.maxSnapshotPixels.width),
    height: this.pixelsToInches(this.maxSnapshotPixels.height),
  };

  imageTypeEnum = ImageTypeEnum;
  snapshotSizes: ScreenModel[] = [
    snapshotSmall,
    snapshotMedium,
    snapshotLarge,
    snapshotExtraLarge,
    snapshotMax,
  ];
  selectedSnapshotSize = snapshotMedium;
  snapshotBackgroundColor: BackgroundModel;

  @Input() model: BookmarkModel;

  @Select(PublishState.getDimensions)
  dimensions$: Observable<ScreenDimensions>;

  @Select(PublishState.getMinDimensions)
  minDimensions$: Observable<ScreenDimensions>;

  @ViewChild(PublishWidgetComponent, { static: true })
  publishWidget: PublishWidgetComponent;

  get rotateEnabled() {
    const rotate = this.formEmbed.get('rotate').value;
    return rotate.enabled;
  }

  get mode(): '2d' | '3d' {
    return this.formModeGroup.get('mode').value;
  }

  get widgetBackgroundColor(): BackgroundModel {
    if (this.mode === '2d') {
      return this.snapshotBackgroundColor;
    } else {
      return this.embedBackgroundColor;
    }
  }

  get widgetScalable(): boolean {
    return this.mode === '2d';
  }

  get imageType(): ImageTypeEnum {
    return this.formSnapshot.get('imageType').value;
  }

  get publishWidgetOptions(): PublishWidgetOptions {
    if (this.formEmbed && this.formSnapshot) {
      switch (this.mode) {
        case '3d':
          return this.widgetEmbedSettings;
        case '2d':
          return this.widgetSnapshotSettings;
      }
    }
    return null;
  }

  get widgetEmbedSettings(): PublishWidgetOptions {
    const dimensionsGroup = this.formEmbed.get('dimensions');
    const widgetOptions = this.formEmbed.value.options || {};

    const options = {
      width: dimensionsGroup.get('width').value,
      height: dimensionsGroup.get('height').value,
      labelScale: 1.0,
      scalable: false,
      backgroundColor: this.embedBackgroundColor,
      options: widgetOptions,
    };

    return options;
  }

  get widgetSnapshotSettings(): PublishWidgetOptions {
    const dimensionsGroup = this.formSnapshot.get('dimensions');
    const labelScaleControl = this.formSnapshot.get('labelScale');
    const widgetOptions = new WidgetOptions();

    widgetOptions.uiTools = false;
    widgetOptions.uiInfo = false;
    widgetOptions.uiNav = false;
    widgetOptions.uiMenu = false;
    widgetOptions.uiAudio = false;
    widgetOptions.uiUndo = false;
    widgetOptions.uiSearch = false;
    widgetOptions.uiWhiteboard = false;
    widgetOptions.uiFullscreen = false;

    const options = {
      width: dimensionsGroup.get('width').value,
      height: dimensionsGroup.get('height').value,
      labelScale: labelScaleControl.value,
      scalable: true,
      backgroundColor: this.snapshotBackgroundColor,
      options: widgetOptions,
    };

    return options;
  }

  formModeGroup: FormGroup;
  formEmbed: FormGroup;
  formSnapshot: FormGroup;
  formQuiz: FormGroup;
  quizQuestions: QuizQuestion[] = [];

  embedBackgroundColor: BackgroundModel;
  labelScale: number;

  private ngUnsubscribe = new Subject();

  // optional: default options
  private defaultWidgetOptions: WidgetOptions = {
    ...new WidgetOptions(),
    loadRotate: 10,
  };

  constructor(
    private store: Store,
    private actions: Actions,
    private publishService: PublishService,
    private storageService: StorageService,
    private clipboardService: ClipboardService,
    private dialogService: DialogService,
    private storyService: StoryService,
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.initFormGroupMode();
    this.initSnapshotForm();
    this.initEmbedForm();
  }

  ngOnInit() {
    this.initColors();
    this.initQuizForm();
    this.initQuizFormListeners();
    this.initActionListeners();

    this.setupEmbedForm();
    this.setupSnapshotForm();

    this.store.dispatch(new SetPublishing(true));
  }

  ngOnDestroy() {
    this.store.dispatch(new SetPublishing(false));

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initFormGroupMode() {
    // mode selection form
    this.formModeGroup = this.formBuilder.group({
      mode: ['3d', Validators.required],
    });
  }

  initSnapshotForm() {
    const snapshotOptions = this.readSnapshotOptions();

    this.formSnapshot = this.formBuilder.group({
      dimensions: this.formBuilder.group({
        width: [
          snapshotOptions.dimensions.width,
          [
            Validators.required,
            Validators.min(this.minSnapshotPixels.width),
            Validators.max(this.maxSnapshotPixels.width),
          ],
        ],
        height: [
          snapshotOptions.dimensions.height,
          [
            Validators.required,
            Validators.min(this.minSnapshotPixels.height),
            Validators.max(this.maxSnapshotPixels.height),
          ],
        ],
      }),
      inches: this.formBuilder.group({
        width: [
          this.pixelsToInches(snapshotOptions.dimensions.width),
          [
            Validators.required,
            Validators.min(this.minSnapshotInches.width),
            Validators.max(this.maxSnapshotInches.width),
          ],
        ],
        height: [
          this.pixelsToInches(snapshotOptions.dimensions.height),
          [
            Validators.required,
            Validators.min(this.minSnapshotInches.height),
            Validators.max(this.maxSnapshotInches.height),
          ],
        ],
      }),
      imageType: [snapshotOptions.imageType, [Validators.required]],
      labelScale: [snapshotOptions.labelScale || 1, [Validators.required]],
    });

    this.updateSnapshotPreset();
  }

  initEmbedForm() {
    const widgetOptions = this.getWidgetOptions();
    const dimensions = this.store.selectSnapshot(PublishState.getDimensions);
    const minDimensions = this.store.selectSnapshot(PublishState.getMinDimensions);
    const { button, handHint, play } = widgetOptions.initial;
    const { alert, overlay } = widgetOptions.disableScroll;

    // widget 3D form
    this.formEmbed = this.formBuilder.group({
      dimensions: this.formBuilder.group({
        width: [dimensions.width, [Validators.min(minDimensions.width)]],
        height: [dimensions.height, [Validators.min(minDimensions.height)]],
      }),
      options: this.formBuilder.group(widgetOptions),
      loader: this.formBuilder.group({
        enabled: widgetOptions.uiLoader !== ViewLoaderEnum.None,
        mode: widgetOptions.uiLoader,
      }),
      rotate: this.formBuilder.group({
        enabled: widgetOptions.loadRotate !== null,
        value: widgetOptions.loadRotate,
      }),
      autoLoad: this.formBuilder.control(widgetOptions.autoLoad),
      initial: this.formBuilder.group({
        enabled: widgetOptions.autoLoad && (button || handHint || play),
        mode: button ? 'button' : handHint ? ('handHint' ? play : 'play') : '',
      }),
      disableScroll: this.formBuilder.group({
        enabled: alert || overlay,
        mode: alert ? 'alert' : overlay ? 'overlay' : '',
      }),
    });
  }

  initColors() {
    const widgetOptions = this.getWidgetOptions();

    // set color
    const colors = get(widgetOptions, 'background.colors');
    if (colors) {
      const color: ColorModel = {
        red: colors[0],
        green: colors[1],
        blue: colors[2],
        alpha: colors.length > 3 ? colors[3] : 1,
      };
      this.embedBackgroundColor = this.getBackgroundFromColor(color);
    }
  }

  initQuizForm() {
    // setup quiz form
    this.formQuiz = this.formBuilder.group({
      locked: [false],
      // locked options
      options: this.formBuilder.group({
        mode: ['all'], // 'all' | 'single'
        number: [1], // question number
      }),
    });

    // check quiz
    const tags = this.model.tags || [];
    const isQuiz = tags.includes(BookmarkFlagsEnum.Quiz);
    if (isQuiz) {
      const { id, type } = this.model;

      // query quiz data
      this.storyService
        .getStory(id, type)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((story) => {
          this.quizQuestions = createQuestionsFromChapters(story.chapters);
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  initQuizFormListeners() {
    const widgetOptionsGroup = this.formEmbed.get('options');
    const quizLockedControl = this.formQuiz.get('locked');
    const quizOptionsControl = this.formQuiz.get('options');
    const quizModeControl = quizOptionsControl.get('mode');
    const quizNumberControl = quizOptionsControl.get('number');

    // setup form interaction
    this.formQuiz.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((values) => {
      let quizMode: ViewQuizModeEnum;
      let quizNumber: number;

      const { locked, options } = values;
      if (locked) {
        const { mode, number } = options;
        quizMode = ViewQuizModeEnum.Locked;
        quizNumber = mode === 'single' ? number : undefined;
      }

      // patch quiz options
      widgetOptionsGroup.patchValue({
        uiQuiz: {
          mode: quizMode,
          number: quizNumber,
        },
      });
    });

    // setup quiz locked
    quizLockedControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((locked) => {
      if (locked) {
        quizOptionsControl.enable();
      } else {
        quizOptionsControl.disable();
      }
    });

    // setup quiz mode
    quizNumberControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const quizLocked: boolean = quizLockedControl.value;
        const quizMode: 'all' | 'single' = quizModeControl.value;
        if (quizLocked === true && quizMode !== 'single') {
          quizModeControl.setValue('single');
        }
      });

    // set quiz unlocked
    quizLockedControl.setValue(false);
  }

  initActionListeners() {
    // Copy url to clipboard
    this.actions
      .pipe(
        ofActionDispatched(GetPublishURLSuccess),
        flatMap((response: GetPublishURLSuccess) => this.copyToClipboard(response.url, 'url')),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((copied) => {
        this.snackBar.open(
          copied ? 'Successfully copied link to clipboard.' : 'Unable to copy link to clipboard.',
        );
      });

    // Copy embed code to clipboard
    this.actions
      .pipe(
        ofActionDispatched(GetPublishIFrameSuccess),
        flatMap((response: GetPublishIFrameSuccess) =>
          this.copyToClipboard(response.embed, 'embed'),
        ),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((copied) => {
        this.snackBar.open(
          copied
            ? 'Successfully copied embed code to clipboard.'
            : 'Unable to copy embed code to clipboard.',
        );
      });

    // Failed to generate embed code
    this.actions
      .pipe(ofActionDispatched(GetPublishIFrameFail), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.snackBar.open(`Unable to generate embed code.`);
      });

    // Copy image url to clipboard
    this.actions
      .pipe(
        ofActionDispatched(SavePublishImageSuccess),
        flatMap((response: SavePublishImageSuccess) =>
          this.copyToClipboard(response.imageURL, 'url'),
        ),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((copied) => {
        this.snackBar.open(
          copied
            ? 'Successfully copied image URL to clipboard.'
            : 'Unable to copy image URL to clipboard.',
        );
      });

    // Faield to generate image url
    this.actions
      .pipe(ofActionDispatched(SavePublishImageFail), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.snackBar.open('Unable to generate image URL.');
      });

    // Copy image embed code to clipboard
    this.actions
      .pipe(
        ofActionDispatched(GetPublishImageCodeSuccess),
        flatMap((response: GetPublishImageCodeSuccess) =>
          this.copyToClipboard(response.embed, 'embed'),
        ),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((copied) => {
        this.snackBar.open(
          copied
            ? 'Successfully copied image embed code to clipboard.'
            : 'Unable to copy image embed code to clipboard.',
        );
      });

    // Failed to generate image embed code
    this.actions
      .pipe(ofActionDispatched(GetPublishImageCodeFail), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.snackBar.open(`Unable to generate embed code.`);
      });
  }

  setupEmbedForm() {
    const optionsGroup = this.formEmbed.get('options');
    const dimensionsGroup = this.formEmbed.get('dimensions');
    const loaderGroup = this.formEmbed.get('loader');
    const rotateGroup = this.formEmbed.get('rotate');
    const initialGroup = this.formEmbed.get('initial');
    const disableScrollGroup = this.formEmbed.get('disableScroll');

    const autoLoadControl = this.formEmbed.get('autoLoad');

    // load progress params
    const loadProgressEnabledField = loaderGroup.get('enabled');
    const loadProgressValueField = loaderGroup.get('mode');

    // set loader type on enabled state
    loadProgressEnabledField.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((enabled) => {
        if (enabled) {
          loadProgressValueField.patchValue(defaultViewerViewLoader);
        } else {
          loadProgressValueField.patchValue(ViewLoaderEnum.None);
        }
      });

    // patch widget options for loader
    loadProgressValueField.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      optionsGroup.patchValue({
        uiLoader: value,
      });
      // enable loader form on valid type
      if (value !== ViewLoaderEnum.None) {
        loadProgressEnabledField.patchValue(true, { emitEvent: false });
      }
    });

    // rotate params
    const rotateEnabledField = rotateGroup.get('enabled');
    const rotateValueField = rotateGroup.get('value');

    // set rotate defaults when toggling rotate enabled
    rotateEnabledField.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((enabled) => {
      if (enabled) {
        // set default widget rotate
        const rotateValue =
          typeof rotateValueField.value === 'number'
            ? rotateValueField.value
            : this.defaultWidgetOptions.loadRotate;
        rotateValueField.patchValue(rotateValue);
      } else {
        // reset widget rotate
        optionsGroup.patchValue({
          loadRotate: null,
        });
      }
    });

    // push rotate changes to widget options
    rotateValueField.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      optionsGroup.patchValue({
        loadRotate: value,
      });
    });

    // sync autoLoad
    autoLoadControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((enabled) => {
      optionsGroup.patchValue({ autoLoad: enabled });

      // disable initial group if autoLoad is disabled
      if (!enabled) {
        initialGroup.patchValue({ enabled: false });
      }
    });

    // sync initial params
    initialGroup
      .get('enabled')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((enabled) => {
        initialGroup.patchValue({
          mode: enabled ? 'button' : '',
        });
      });

    initialGroup
      .get('mode')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((mode) => {
        initialGroup.patchValue({ enabled: !!mode }, { emitEvent: false });
        optionsGroup.patchValue({
          initial: {
            button: mode === 'button',
            handHint: mode === 'handHint',
            play: mode === 'play',
          },
        });
      });

    // sync scroll params
    disableScrollGroup
      .get('enabled')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((enabled) => {
        disableScrollGroup.patchValue({
          mode: enabled ? 'alert' : '',
        });
      });

    disableScrollGroup
      .get('mode')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((mode) => {
        disableScrollGroup.patchValue({ enabled: !!mode }, { emitEvent: false });
        optionsGroup.patchValue({
          disableScroll: {
            alert: mode === 'alert',
            overlay: mode === 'overlay',
          },
        });
      });

    // sync dimensions
    dimensionsGroup.valueChanges
      .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe))
      .subscribe((dimensions) => {
        this.setScreenDimensions(dimensions);
      });

    this.dimensions$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((dimensions) => {
      dimensionsGroup.setValue(dimensions, { emitEvent: false });
    });

    optionsGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((options) => {
      // persist options
      this.writeEmbedOptions(options);
    });
  }

  setupSnapshotForm() {
    const formSnapshotDimensionsGroup = this.formSnapshot.get('dimensions');
    const formSnapshotInchesGroup = this.formSnapshot.get('inches');
    const labelScaleControl = this.formSnapshot.get('labelScale');

    this.formSnapshot.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(50))
      .subscribe((values: any) => {
        this.labelScale = labelScaleControl.value;

        const snapshotOptions = plainToClass(SnapshotOptions, values);
        this.writeSnapshotOptions(snapshotOptions);
        this.updateSnapshotPreset();

        this.changeDetectorRef.markForCheck();
      });

    // The snapshot inches control changes the size indirectly by modifying the pixel dimensions value.
    // It is not stored or submitted anywhere.
    formSnapshotInchesGroup.valueChanges
      .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const { width, height } = formSnapshotInchesGroup.value;

        if (formSnapshotInchesGroup.valid) {
          formSnapshotDimensionsGroup.patchValue({
            width: this.inchesToPixels(width),
            height: this.inchesToPixels(height),
          });
        }
      });
  }

  setScreenDimensions(dimensions: ScreenDimensions) {
    this.store.dispatch(new SetPublishDimensions(dimensions));
  }

  handleGenerateIFrame() {
    this.generateIFrameCode();
  }

  handleGenerateURL() {
    this.generateURL();
  }

  handleCopyImageURL() {
    this.generateImageURL();
  }

  handleGenerateImage() {
    this.generateImageCode();
  }

  handleResetScale() {
    this.formSnapshot.get('labelScale').setValue(1.0);
  }

  generateIFrameCode() {
    const widgetOptions = this.formEmbed.value.options;
    this.store.dispatch(new GetPublishIFrame(this.model, widgetOptions));
  }

  generateURL() {
    const widgetOptions = this.formEmbed.value.options;
    this.store.dispatch(new GetPublishURL(this.model, widgetOptions));
  }

  generateImageURL() {
    const fileType = this.imageType === ImageTypeEnum.PNG ? 'png' : 'jpeg';

    this.publishWidget
      .takeSnapshot(fileType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((imageData) => {
        this.store.dispatch(new SavePublishImage(imageData, fileType));
      });
  }

  generateImageCode() {
    const fileType = this.imageType === ImageTypeEnum.PNG ? 'png' : 'jpeg';

    this.publishWidget
      .takeSnapshot(fileType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((imageData) => {
        const widgetOptions = new WidgetOptions(); // use default options
        this.store.dispatch(
          new GetPublishImageCode(this.model, imageData, fileType, widgetOptions),
        );
      });
  }

  handleSnapshotBackgroundSelected(color: ColorModel) {
    this.snapshotBackgroundColor = this.getBackgroundFromColor(color);
    this.changeDetectorRef.markForCheck();
  }

  handleSnapshotSizeClick(screen: ScreenModel) {
    this.selectedSnapshotSize = {
      ...screen,
    };
    this.formSnapshot.patchValue(
      {
        dimensions: {
          width: screen.dimensions.width,
          height: screen.dimensions.height,
        },
        inches: {
          width: this.pixelsToInches(screen.dimensions.width),
          height: this.pixelsToInches(screen.dimensions.height),
        },
      },
      { emitEvent: false },
    );
  }

  handleDownloadSnapClick() {
    const fileType = this.imageType === ImageTypeEnum.PNG ? 'png' : 'jpeg';

    // When inches is selected, set DPI to 300
    // This ony affects the metadata of JPEG images
    const tabItem = <TabItem>this.imageTabs.tabItems[this.imageTabs.activeTab];
    const customDpi = tabItem.label === 'Inches' ? 300 : null;

    this.publishWidget
      .takeSnapshot(fileType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((imageData) => {
        this.downloadImage(imageData, customDpi);
      });
  }

  copyToClipboard(copyText: string, type: CopyClipboardType): Observable<boolean> {
    return this.clipboardService.copyToClipboard(copyText, type).pipe(
      flatMap((copied) => {
        if (copied) {
          return of(copied);
        } else {
          return this.dialogService.openClipboardDialog(copyText).afterClosed();
        }
      }),
    );
  }

  formatScreenSizeText(screen: ScreenModel): string {
    const { title, dimensions } = screen;
    return `${title} (${dimensions.width} x ${dimensions.height})`;
  }

  hasVisibilityLayers() {
    return this.publishWidget.allVisibilityLayers.some((visibilityLayers) => {
      return Array.isArray(visibilityLayers) && visibilityLayers.length > 0;
    });
  }

  private updateSnapshotPreset() {
    const formSnapshotDimensionsGroup = this.formSnapshot.get('dimensions');
    const { width, height } = formSnapshotDimensionsGroup.value;

    if (formSnapshotDimensionsGroup.valid) {
      // Check the dimensions and see if they match any presets in the dropdown.
      // If a match is found, select it. Otherwise write out the custom value.
      const foundSnap = this.snapshotSizes.find(
        (snap) => snap.dimensions.width === width && snap.dimensions.height === height,
      );

      this.selectedSnapshotSize = {
        ...this.selectedSnapshotSize,
        title: foundSnap ? foundSnap.title : 'Custom',
        dimensions: {
          width: width,
          height: height,
        },
      };

      this.changeDetectorRef.markForCheck();
    }
  }

  private getBackgroundFromColor(color: ColorModel): BackgroundModel {
    const colors = color ? [color.red, color.green, color.blue, color.alpha] : null;
    if (colors) {
      return new BackgroundModel([...colors, ...colors], 'radial', [0, 100]);
    } else {
      return null;
    }
  }

  private getWidgetOptions(): WidgetOptions {
    // read persisted options
    const storedOptions = this.readEmbedOptions();

    // read organization settings
    const orgWidgetOptions = this.store.selectSnapshot(OrganizationsState.getWidgetOptions);

    // apply saved settings
    if (storedOptions) {
      const userOptions = this.publishService.extendWidgetOptions(
        new WidgetOptions(),
        storedOptions,
      );
      return {
        ...userOptions,
        // !important: disable user background, expect from organization
        background: orgWidgetOptions ? orgWidgetOptions.background : null,
      };
    } else {
      return orgWidgetOptions || new WidgetOptions();
    }
  }

  private readEmbedOptions(): any {
    // invalidate widget options in local storage created before
    // Wed Nov 13 2019 14:57:47 GMT-0500 (Eastern Standard Time)
    const invalidateBefore = 1573675067505;
    const userProfileId = this.getStorageKey();
    invalidateLocalWidgetOptions(userProfileId, invalidateBefore);

    // read persisted options
    const storedOptions = this.storageService.getLocalJSON(LocalStorageEnum.WidgetOptions);
    return storedOptions ? storedOptions[this.getStorageKey()] : null;
  }

  private writeEmbedOptions(options: WidgetOptions) {
    // normalize persisted options: trim/adjust as needed
    const normalizedOptions = {
      ...options,
    };
    delete normalizedOptions['uiQuiz'];

    // patch options with a timestamp
    const optionsWithTimestamp = { ...normalizedOptions, timestamp: Date.now() };

    // modify existing entry
    const storedOptions = this.storageService.getLocalJSON(LocalStorageEnum.WidgetOptions) || {};
    const key = this.getStorageKey();
    storedOptions[key] = optionsWithTimestamp;
    this.storageService.setLocalItem(LocalStorageEnum.WidgetOptions, storedOptions);
  }

  private writeSnapshotOptions(options: any) {
    // modify existing entry
    const storedOptions = this.storageService.getLocalJSON(LocalStorageEnum.SnapshotOptions) || {};
    const key = this.getStorageKey();
    storedOptions[key] = options;
    this.storageService.setLocalItem(LocalStorageEnum.SnapshotOptions, storedOptions);
  }

  private readSnapshotOptions(): SnapshotOptions {
    // read persisted options
    let storedOptions = this.storageService.getLocalJSON(LocalStorageEnum.SnapshotOptions);
    storedOptions = storedOptions ? storedOptions[this.getStorageKey()] : {};

    const newOptions = new SnapshotOptions();

    // Merge persisted and new options, since the data structure in new options
    // may vary from a legacy cookie.
    const result = {
      ...newOptions,
      ...storedOptions,
    };

    return result;
  }

  // get unique key for widget settings
  private getStorageKey(): string {
    return this.store.selectSnapshot(AuthState.getUserProfile).eid;
  }

  private downloadImage(imageData: string, customDpi?: number) {
    const title = this.model.title || 'snapshot';
    const normalizedTitle = title.replace(/ /g, '_').replace(/\W/g, '');

    const extension = this.imageType === ImageTypeEnum.PNG ? 'png' : 'jpg';
    const fileName = `${normalizedTitle}.${extension}`;

    ExportImageUtils.exportImage(imageData, this.imageType, fileName, customDpi)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((success) => {
        this.snackBar.open(success ? `Successfuly downloaded image` : `Unable to download image.`);
      });
  }

  private pixelsToInches(pixels: number): number {
    return parseFloat((pixels / ppi).toFixed(2));
  }

  private inchesToPixels(inches: number): number {
    return inches * ppi;
  }
}
